import { isPageContextEnabled } from "./pageContext";

/** Long enough for any real path, short enough to cap junk. */
const MAX_TARGET_PATH_LENGTH = 256;

/**
 * The element a click should be attributed to. Links and buttons here wrap
 * spans, icons and images, so the click usually lands on a child of the `<a>`
 * or `<button>`; with `NEXT_PUBLIC_SF_PAGE_CONTEXT` on, that click counts as a
 * click on the enclosing link or button; a link wins over a button, because
 * the link is what carries the destination. With the flag off the click target
 * is returned untouched, which keeps the sitemap template's own-tag-only rule.
 *
 * Call it while the click is still travelling down to its target (a capture
 * listener): by the time the click bubbles up, a handler may already have
 * replaced the clicked icon or label, and an element that is no longer in the
 * page has no enclosing link or button left to find.
 */
export function resolveClickTarget(target: EventTarget | null): EventTarget | null {
    if (!isPageContextEnabled() || !(target instanceof Element)) return target;
    return target.closest("a") ?? target.closest("button") ?? target;
}

/**
 * Where a clicked link goes, for the optional `interaction.targetPath`
 * attribute on click events. Returns null unless `NEXT_PUBLIC_SF_PAGE_CONTEXT`
 * is on and the click is on an `<a href>`.
 *
 * What is kept, so no personal data rides along in a link:
 * - same-site link: the path only (`/work/acme`), no query string, no fragment
 * - external link: origin and path (`https://example.com/page`), no query
 *   string, fragment or credentials
 * - mailto:, tel:, sms: and similar: the scheme only (`mailto:`), never the
 *   address or number
 * - fragment-only link (`#main-content`): nothing, it goes nowhere new
 */
export function clickTargetPath(target: EventTarget | null): string | null {
    if (!isPageContextEnabled()) return null;
    if (!(target instanceof Element) || target.tagName.toLowerCase() !== "a") return null;

    const href = target.getAttribute("href")?.trim();
    if (!href || href.startsWith("#")) return null;

    let url: URL;
    try {
        url = new URL(href, document.baseURI);
    } catch {
        return null;
    }

    if (url.protocol !== "http:" && url.protocol !== "https:") {
        return url.protocol;
    }

    const path = url.origin === window.location.origin ? url.pathname : `${url.origin}${url.pathname}`;
    return path.slice(0, MAX_TARGET_PATH_LENGTH);
}
